import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {  
  X,
  Users,
  Calendar,
  Sparkles,
  Mail,
  Phone,
  MapPin,
  Clock,
  DollarSign,
  ChevronRight,
  FileText,
  CheckCircle,
  ShoppingBag
} from 'lucide-react';

export default function App() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState<'calendar' | 'shop' | 'consultation' | 'consent' | null>(null);
  const [formStep, setFormStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    desiredTreatment: '',
    skinConcerns: '',
    preferredDate: '',
    preferredTime: '',
    alternateDate: '',
    previousTreatments: '',
    allergies: '',
    questions: ''
  });

  // Handle form input changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmitForm = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Send form data to the server API
      const response = await fetch('http://localhost:3001/api/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Error submitting form');
      }
      
      // Success
      setSubmitSuccess(true);
      setFormStep(1);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        desiredTreatment: '',
        skinConcerns: '',
        preferredDate: '',
        preferredTime: '',
        alternateDate: '',
        previousTreatments: '',
        allergies: '',
        questions: ''
      });
      
      // Close modal after success
      setTimeout(() => {
        setShowModal(null);
        setSubmitSuccess(false);
      }, 3000);
      
    } catch (error) {
      setSubmitError('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal opening
  const handleModal = (modalType: 'calendar' | 'shop' | 'consultation' | 'consent') => {
    setShowModal(modalType);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/90 backdrop-blur-sm z-50 border-b border-primary-100 shadow-sm">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <Sparkles className="text-primary-600 w-8 h-8 mr-2" />
              <h1 className="text-2xl font-serif text-primary-900 tracking-wide">Lola's Glow Skin & Beauty</h1>
            </div>
            <div className="hidden md:flex space-x-8 items-center">
              <a 
                href="#treatments" 
                onClick={(e) => { e.preventDefault(); }}
                className="text-primary-700 hover:text-primary-600 transition-colors font-medium text-lg"
              >
                Treatments
              </a>
              <a 
                href="#about" 
                onClick={(e) => { e.preventDefault(); }}
                className="text-primary-700 hover:text-primary-600 transition-colors font-medium text-lg"
              >
                About
              </a>
              <a 
                href="#contact" 
                onClick={(e) => { e.preventDefault(); }}
                className="text-primary-700 hover:text-primary-600 transition-colors font-medium text-lg"
              >
                Contact
              </a>
              <div className="flex space-x-4">
                <button 
                  onClick={() => handleModal('calendar')}
                  className="bg-primary-600 text-white px-6 py-3 hover:bg-primary-700 transition-all duration-300 font-medium rounded-lg shadow-sm hover:shadow-md"
                >
                  Book Now
                </button>
                <button 
                  onClick={() => navigate('/product-guide')}
                  className="bg-primary-800 text-white px-6 py-3 hover:bg-primary-700 transition-all duration-300 font-medium rounded-lg shadow-sm hover:shadow-md flex items-center"
                >
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Shop
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-20 bg-cream-50 relative">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-7xl font-serif text-primary-900 mb-6 leading-tight">
                Radiant Beauty <br />
                <span className="text-orange-600">For Your Skin</span>
              </h1>
              <p className="text-xl text-primary-600 mb-12 font-medium leading-relaxed">
                Discover our premium treatments and products for a natural, healthy glow.
              </p>
              <div className="bg-white/90 backdrop-blur-sm border border-primary-200 p-8 mb-12 rounded-xl shadow-lg">
                <h3 className="text-primary-900 text-2xl mb-6 font-medium">NEW: Morning Glow Lotion</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <img 
                      src="https://images.unsplash.com/photo-1601049676869-702ea24cfd58?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80" 
                      alt="Morning Glow Lotion" 
                      className="rounded-lg shadow-md object-cover h-full w-full"
                    />
                  </div>
                  <div>
                    <p className="text-primary-900 font-semibold mb-4 text-lg">Handcrafted with Premium Ingredients:</p>
                    <ul className="space-y-2">
                      <li className="flex items-center text-primary-700">
                        <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                        Hyaluronic Acid for Deep Hydration
                      </li>
                      <li className="flex items-center text-primary-700">
                        <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                        Vitamin C for Brightening
                      </li>
                      <li className="flex items-center text-primary-700">
                        <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                        Niacinamide for Texture Improvement
                      </li>
                      <li className="flex items-center text-primary-700">
                        <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                        Peptides for Anti-Aging
                      </li>
                    </ul>
                    <button 
                      onClick={() => window.location.href = '/shop'}
                      className="mt-6 bg-primary-800 text-white px-6 py-3 hover:bg-primary-700 transition-all duration-300 font-medium rounded-lg shadow-sm hover:shadow-md flex items-center justify-center w-full"
                    >
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Shop Now - $48
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6">
                <button 
                  onClick={() => handleModal('calendar')}
                  className="bg-primary-600 text-white px-8 py-4 text-lg font-medium flex items-center group transition-all duration-300 hover:bg-primary-700 rounded-lg shadow-sm hover:shadow-md"
                >
                  Book Treatment
                  <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
                <button 
                  onClick={() => handleModal('consultation')}
                  className="bg-orange-100 text-primary-800 px-8 py-4 text-lg font-medium transition-all duration-300 hover:bg-orange-200 rounded-lg shadow-sm hover:shadow-md border border-orange-300"
                >
                  Free Consultation
                </button>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1560750588-73207b1ef5b8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80" 
                alt="Lola's Glow Spa" 
                className="rounded-xl shadow-lg object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Treatments Section */}
      <div id="treatments" className="py-20 bg-primary-50 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-primary-900">Glow-Enhancing Treatments</h2>
            <p className="text-xl text-primary-600 max-w-3xl mx-auto">
              Our signature treatments are designed to reveal your skin's natural radiance and beauty.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div 
              className="bg-white/90 backdrop-blur-sm p-8 border border-primary-200 hover:border-primary-300 transform hover:-translate-y-2 transition-all duration-300 rounded-xl shadow-md hover:shadow-lg relative overflow-hidden group"
            >
              {/* Decorative Elements */}
              <div className="absolute -right-16 -top-16 w-32 h-32 bg-cream-100 rounded-full opacity-70"></div>
              <div className="absolute -left-16 -bottom-16 w-32 h-32 bg-orange-100 rounded-full opacity-70"></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="mb-6 rounded-lg overflow-hidden shadow-md">
                  <img 
                    src="/PRP.png" 
                    alt="PRP/PRF Treatment" 
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h3 className="text-3xl font-serif mb-4 text-primary-900">PRP/PRF Treatment</h3>
                <p className="text-primary-600 mb-4">Advanced platelet-rich plasma/fibrin therapy for natural skin rejuvenation and facial enhancement</p>
                <div className="flex items-center text-primary-700 mb-4 bg-primary-100 p-3 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-500 mr-2" />
                  <span>Duration: 90 minutes</span>
                </div>
                <div className="flex items-center text-primary-700 mb-4 bg-primary-100 p-3 rounded-lg">
                  <DollarSign className="w-5 h-5 text-orange-500 mr-2" />
                  <span>Price: $350</span>
                </div>
                
                <div className="text-primary-600 mb-6 text-sm border border-primary-200 p-4 rounded-lg bg-primary-50/50 backdrop-blur-sm">
                  <Users className="w-5 h-5 inline-block mr-2 text-orange-500" />
                  Book with 3+ people and save $100 each!
                </div>
                
                <ul className="space-y-4 mb-8 bg-white/50 backdrop-blur-sm p-6 rounded-xl border border-primary-200">
                  <li className="flex items-center text-primary-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                    Natural collagen stimulation
                  </li>
                  <li className="flex items-center text-primary-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                    Improved skin texture
                  </li>
                  <li className="flex items-center text-primary-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                    Enhanced healing factors
                  </li>
                  <li className="flex items-center text-primary-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                    Long-lasting results
                  </li>
                </ul>
                
                <div className="flex space-x-4">
                  <button 
                    onClick={() => handleModal('calendar')}
                    className="bg-primary-600 text-white px-6 py-3 hover:bg-primary-700 transition-colors rounded-lg flex-1 flex items-center justify-center"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Now
                  </button>
                  <button 
                    onClick={() => handleModal('consent')}
                    className="bg-cream-100 text-primary-800 px-6 py-3 hover:bg-cream-200 transition-colors rounded-lg border border-primary-200 flex items-center justify-center"
                  >
                    <FileText className="w-5 h-5 mr-2 text-orange-500" />
                    Details
                  </button>
                </div>
              </div>
            </div>
            <div 
              className="bg-white/90 backdrop-blur-sm p-8 border border-primary-200 hover:border-primary-300 transform hover:-translate-y-2 transition-all duration-300 rounded-xl shadow-md hover:shadow-lg relative overflow-hidden group"
            >
              {/* Decorative Elements */}
              <div className="absolute -right-16 -top-16 w-32 h-32 bg-orange-100 rounded-full opacity-70"></div>
              <div className="absolute -left-16 -bottom-16 w-32 h-32 bg-cream-100 rounded-full opacity-70"></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="mb-6 rounded-lg overflow-hidden shadow-md">
                  <img 
                    src="/RedLight.png" 
                    alt="Red Light Therapy" 
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h3 className="text-3xl font-serif mb-4 text-primary-900">Red Light Therapy</h3>
                <p className="text-primary-600 mb-4">Advanced LED light therapy for skin rejuvenation and tissue healing</p>
                <div className="flex items-center text-primary-700 mb-4 bg-primary-100 p-3 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-500 mr-2" />
                  <span>Duration: 30 minutes</span>
                </div>
                <div className="flex items-center text-primary-700 mb-4 bg-primary-100 p-3 rounded-lg">
                  <DollarSign className="w-5 h-5 text-orange-500 mr-2" />
                  <span>Price: $75</span>
                </div>
                
                <ul className="space-y-4 mb-8 bg-white/50 backdrop-blur-sm p-6 rounded-xl border border-primary-200">
                  <li className="flex items-center text-primary-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                    Reduces inflammation
                  </li>
                  <li className="flex items-center text-primary-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                    Promotes collagen production
                  </li>
                  <li className="flex items-center text-primary-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                    Improves skin tone
                  </li>
                  <li className="flex items-center text-primary-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                    Non-invasive treatment
                  </li>
                </ul>
                
                <div className="flex space-x-4">
                  <button 
                    onClick={() => handleModal('calendar')}
                    className="bg-primary-600 text-white px-6 py-3 hover:bg-primary-700 transition-colors rounded-lg flex-1 flex items-center justify-center"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Now
                  </button>
                  <button 
                    onClick={() => handleModal('consent')}
                    className="bg-cream-100 text-primary-800 px-6 py-3 hover:bg-cream-200 transition-colors rounded-lg border border-primary-200 flex items-center justify-center"
                  >
                    <FileText className="w-5 h-5 mr-2 text-orange-500" />
                    Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div id="about" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1560750588-73207b1ef5b8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80" 
                alt="Lola's Glow Spa" 
                className="rounded-xl shadow-lg object-cover w-full h-full"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-primary-900">About Lola's Glow</h2>
              <p className="text-lg text-primary-700 mb-6 leading-relaxed">
                Founded with a passion for natural beauty and skincare, Lola's Glow is dedicated to helping you achieve your best skin through advanced treatments and premium products.
              </p>
              <p className="text-lg text-primary-700 mb-6 leading-relaxed">
                Our approach combines cutting-edge technology with natural ingredients to deliver exceptional results. We believe in enhancing your natural beauty, not masking it.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-cream-100 p-6 rounded-lg border-l-4 border-orange-400">
                  <h3 className="text-xl font-semibold mb-2 text-primary-900">Our Mission</h3>
                  <p className="text-primary-700">To empower everyone to feel confident in their skin through personalized care.</p>
                </div>
                <div className="bg-cream-100 p-6 rounded-lg border-l-4 border-orange-400">
                  <h3 className="text-xl font-semibold mb-2 text-primary-900">Our Promise</h3>
                  <p className="text-primary-700">Quality, transparency, and results you can see and feel.</p>
                </div>
              </div>
              <button 
                onClick={() => handleModal('consultation')}
                className="bg-primary-600 text-white px-8 py-4 hover:bg-primary-700 transition-colors rounded-lg flex items-center"
              >
                Schedule a Consultation
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="py-20 bg-cream-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif text-primary-900 mb-4">Get in Touch</h2>
            <p className="text-primary-600 max-w-2xl mx-auto">
              Have questions about our treatments or ready to book your appointment? 
              Reach out to us and we'll get back to you as soon as possible.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 mb-16 border-t-4 border-orange-400">
            <div className="space-y-8">
              <div className="flex space-x-8 mb-8 justify-center">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      step === 1 ? 'bg-primary-600 text-white' : 'bg-cream-200 text-primary-500'
                    }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-primary-900">Personal Information</h3>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Your Name"
                  className="w-full px-4 py-3 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg transition-all"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  className="w-full px-4 py-3 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg transition-all"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  className="w-full px-4 py-3 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg transition-all"
                />
              </div>
              
              <div className="flex justify-between mt-8">
                <button
                  className="px-6 py-3 bg-cream-200 text-primary-900 hover:bg-cream-300 transition-colors ml-auto rounded-lg"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-primary-900 text-white py-16 mt-8">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-2xl font-serif mb-6">Lola's Glow</h3>
              <p className="text-cream-300 mb-6">
                Advanced skincare treatments and premium products to enhance your natural beauty.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-6">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <a href="#treatments" className="text-cream-300 hover:text-orange-300 transition-colors">Treatments</a>
                </li>
                <li>
                  <a href="/product-guide" className="text-cream-300 hover:text-orange-300 transition-colors">Shop</a>
                </li>
                <li>
                  <a href="#contact" className="text-cream-300 hover:text-orange-300 transition-colors">Contact</a>
                </li>
                <li>
                  <a href="#" className="text-cream-300 hover:text-orange-300 transition-colors">Privacy Policy</a>
                </li>
                <li>
                  <a href="#" className="text-cream-300 hover:text-orange-300 transition-colors">Terms of Service</a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-6">Contact Us</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-cream-300">
                  <MapPin className="w-5 h-5 mr-3 text-orange-400" />
                  <span>Sparta, NJ</span>
                </li>
                <li className="flex items-center text-cream-300">
                  <Phone className="w-5 h-5 mr-3 text-orange-400" />
                  <span>(862) 686-2211</span>
                </li>
                <li className="flex items-center text-cream-300">
                  <Mail className="w-5 h-5 mr-3 text-orange-400" />
                  <span>Doris@LolasGlow.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-primary-800 mt-12 pt-8 text-center text-cream-400">
            <p>&copy; {new Date().getFullYear()} Lola's Glow Skin & Beauty. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-primary-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-serif text-primary-900">
                {showModal === 'calendar' ? 'Book Your Treatment' :
                 showModal === 'consultation' ? 'Free Consultation' :
                 showModal === 'consent' ? 'Treatment Consent' :
                 showModal === 'shop' ? 'Shop Products' : ''}
              </h2>
              <button 
                onClick={() => setShowModal(null)}
                className="text-primary-500 hover:text-primary-700 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex space-x-8 mb-8">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      formStep >= step ? 'bg-primary-600 text-white' : 'bg-cream-200 text-primary-500'
                    }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
              
              {formStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-primary-900">Personal Information</h3>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Your Name"
                    value={formData.fullName}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  />
                </div>
              )}
              
              {formStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-primary-900">Treatment Interest</h3>
                  <select
                    name="desiredTreatment"
                    value={formData.desiredTreatment}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  >
                    <option value="">Select Treatment</option>
                    <option value="prp">PRP/PRF Treatment</option>
                    <option value="redlight">Red Light Therapy</option>
                  </select>
                  <textarea
                    name="skinConcerns"
                    placeholder="Tell us about your skin concerns"
                    value={formData.skinConcerns}
                    onChange={handleFormChange}
                    rows={4}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  ></textarea>
                </div>
              )}
              
              {formStep === 3 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-primary-900">Preferred Schedule</h3>
                  <select
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  >
                    <option value="">Select Preferred Time</option>
                    <option value="morning">Morning (9AM - 12PM)</option>
                    <option value="afternoon">Afternoon (12PM - 4PM)</option>
                    <option value="evening">Evening (4PM - 7PM)</option>
                  </select>
                  <input
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  />
                  <input
                    type="date"
                    name="alternateDate"
                    placeholder="Alternate Date"
                    value={formData.alternateDate}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  />
                </div>
              )}
              
              {formStep === 4 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-primary-900">Additional Information</h3>
                  <textarea
                    name="previousTreatments"
                    placeholder="Have you had any previous skin treatments?"
                    value={formData.previousTreatments}
                    onChange={handleFormChange}
                    rows={2}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  ></textarea>
                  <textarea
                    name="allergies"
                    placeholder="Do you have any allergies or skin sensitivities?"
                    value={formData.allergies}
                    onChange={handleFormChange}
                    rows={2}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  ></textarea>
                  <textarea
                    name="questions"
                    placeholder="Any questions or additional information?"
                    value={formData.questions}
                    onChange={handleFormChange}
                    rows={4}
                    className="w-full px-4 py-2 bg-cream-50 border border-cream-300 text-primary-900 placeholder-primary-400 focus:ring-2 focus:ring-primary-500 rounded-lg"
                  ></textarea>
                </div>
              )}
              
              <div className="flex justify-between mt-8">
                {formStep > 1 && (
                  <button
                    onClick={() => setFormStep(prev => prev - 1)}
                    className="px-6 py-2 bg-cream-200 text-primary-900 hover:bg-cream-300 transition-colors rounded-lg"
                  >
                    Previous
                  </button>
                )}
                {formStep < 4 ? (
                  <button
                    onClick={() => setFormStep(prev => prev + 1)}
                    className="px-6 py-2 bg-primary-600 text-white hover:bg-primary-500 transition-colors ml-auto rounded-lg"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitForm}
                    className={`px-6 py-2 bg-primary-600 text-white hover:bg-primary-500 transition-colors ml-auto rounded-lg flex items-center ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-2">⌛</span>
                        Submitting...
                      </>
                    ) : 'Submit'}
                  </button>
                )}
              </div>
              
              {submitError && (
                <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg">
                  {submitError}
                </div>
              )}
              {submitSuccess && (
                <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-lg">
                  Thank you for your booking request! We'll contact you shortly to confirm your appointment.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
